import { useRef, useState, useEffect, useCallback } from "react";
import classes from "./EditorContainer.module.css";
import PreisEditor from "./Pickers/PreisEditor";
import StandortEditor from "./Pickers/StandortEditor";
import TypeOfStudioEditor from "./Pickers/TypeOfStudioEditor";
import Stornierungsbedingungen from "../StudioErstellen/Stornierungsbedingungen";
import Rules from "../StudioErstellen/Rules";
import AvailabilityEditor from "./Pickers/AvailabilityEditor";
import EquipmentOptionalEditor from "./Pickers/EquipmentOptionalEditor";
import StudioEquipmentEditor from "./Pickers/StudioEquipmentEditor";
import StatusEditor from "./Pickers/StatusEditor";
import StudioSizeEditor from "./Pickers/StudioSizeEditor";
import AmenitiesEditor from "./Pickers/AmenitiesEditor";
// import TagsEditor from "./Pickers/TagsEditor";
import TitleDescription from "./Pickers/TitleDescription";
import FotosEditor from "./Pickers/FotosEditor";
import { useUpdateStudio } from "../../hooks/useUpdateStudio";
import { useStudioEditor } from "../../store/studioEditorStore";
import SaveNotification from "../UI/SaveNotification/SaveNotification";
import { syncStudioEditorPaidEquipmentChanges } from "./studioEditorPaidEquipmentFlow";
import { saveStudioEditorPhotos } from "./studioEditorPhotoFlow";
import { useApolloClient, useMutation } from "@apollo/client";
import {
    CREATE_PAID_EQUIPMENT_ITEM,
    UPDATE_PAID_EQUIPMENT_ITEM,
    DELETE_PAID_EQUIPMENT_ITEM,
} from "../../graphql/queries/createStudioQueries";
import { trackStudioUpdated } from "../../util/tracking";
import { notifySaveError } from "../../util/notifyActionError";

export const EditorContainer = ({
        studio,
        activeComponent,
        leftPosition,
        isMobileView,
        handleBackClick,
        onEditorDirtyChange,
        registerSaveHandler
    }) => {

    const [isUploading, setIsUploading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [buttonRightPosition, setButtonRightPosition] = useState(null);
    const contentRef = useRef(null);
    const { updatedFields, clearUpdates } = useStudioEditor();
    const { handleStudioUpdate, loading } = useUpdateStudio();
    const client = useApolloClient();
    const [createPaidEquipmentItem] = useMutation(CREATE_PAID_EQUIPMENT_ITEM);
    const [updatePaidEquipmentItem] = useMutation(UPDATE_PAID_EQUIPMENT_ITEM);
    const [deletePaidEquipmentItem] = useMutation(DELETE_PAID_EQUIPMENT_ITEM);
    const availabilityEditorRef = useRef(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [showSaveNotification, setShowSaveNotification] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const standortValidation = useRef(() => true);
    const typeOfStudioValidation = useRef(() => true);
    // const beschreibungValidation = useRef(() => true);
    const fotosInputValidation = useRef(() => true);
    const studioSizeValidation = useRef(() => true);
    const studioEquipmentValidation = useRef(() => true);
    const amenitiesValidation = useRef(() => true);
    const stornierungsbedingungenValidation = useRef(() => true);
    const rulesValidation = useRef(() => true);
    const preisValidation = useRef(() => true);
    const availabilityValidation = useRef(() => true);
    const equipmentOptionalValidation = useRef(() => true);
    const titleDescriptionValidation = useRef(() => true);
    const statusValidation = useRef(() => true);

    const validateCurrentSlide = async () => {
        // If no active component is selected, allow saving without validation
        if (!activeComponent) {
            return true;
        }

        // Map active component to its validation function
        const validationFunctionMap = {
            'fotos': fotosInputValidation.current,
            'titel': titleDescriptionValidation.current,
            'standort': standortValidation.current,
            'studioType': typeOfStudioValidation.current,
            'size': studioSizeValidation.current,
            'price': preisValidation.current,
            // 'tags': beschreibungValidation.current,
            'equipment': studioEquipmentValidation.current,
            'amenities': amenitiesValidation.current,
            'cancellation': stornierungsbedingungenValidation.current,
            'rules': rulesValidation.current,
            'availability': availabilityValidation.current,
            'equipmentOptional': equipmentOptionalValidation.current,
            'status': statusValidation.current
        };

        // Get the validation function for the active component
        const validateFunction = validationFunctionMap[activeComponent];

        if (!validateFunction) {
            return true;
        }

        // Only validate the active component
        const validationResult = await validateFunction();
        // For fotos component, we need to check the isValid property
        if (activeComponent === 'fotos') {
            setHasError(!validationResult.isValid);
            return validationResult;
        }

        // For other components, the result is a boolean
        setHasError(!validationResult);
        return validationResult;
    };

    const syncOptionalEquipmentChanges = async (stagedItems) => {
        await syncStudioEditorPaidEquipmentChanges({
            studio,
            stagedItems,
            createPaidEquipmentItem,
            updatePaidEquipmentItem,
            deletePaidEquipmentItem,
            refetchQueries: client.refetchQueries.bind(client),
        });
    };

    const handleSaveChanges = async () => {
        if (activeComponent === 'fotos') {
            const validationResult = await validateCurrentSlide();

            if (validationResult && validationResult.isValid) {
                try {
                    setIsUploading(true);
                    setHasError(false);

                    await saveStudioEditorPhotos({
                        studio,
                        selectedFiles: validationResult.selectedFiles,
                        updateStudio: handleStudioUpdate,
                    });
                    trackStudioUpdated({
                        studioId: studio?.id,
                        section: 'fotos',
                        source: 'studio_editor'
                    });
                    clearUpdates();
                    setShowSaveNotification(true);
                    setIsUploading(false);
                    return true;
                } catch (error) {
                    if (error.cleanupError) {
                        console.warn('Studio-Foto-Cleanup nach fehlgeschlagenem Studio-Update ist fehlgeschlagen:', error.cleanupError);
                    }
                    setIsUploading(false);
                    setHasError(true);
                    notifySaveError(error, {
                        title: 'Fotos konnten nicht gespeichert werden',
                        onRetry: () => handleSaveChanges(),
                    });
                    return false;
                }
            }
            return false;
        }

        // First check if there are any errors in the AvailabilityEditor
        if (activeComponent === 'availability' &&
            availabilityEditorRef.current &&
            availabilityEditorRef.current.hasErrors) {

            const hasErrors = availabilityEditorRef.current.hasErrors();

            if (hasErrors) {
                setHasError(true);
                return false;
            }
        }

        if (!(await validateCurrentSlide())) {
            return false;
        }

        try {
            setIsUploading(true);
            setHasError(false);

            if (activeComponent === 'availability' && availabilityEditorRef.current) {
                const saveResult = await availabilityEditorRef.current.handleSave();

                if (!saveResult) {
                    setHasError(true);
                    notifySaveError(
                        new Error('Verfügbarkeit konnte nicht gespeichert werden.'),
                        {
                            title: 'Verfügbarkeit konnte nicht gespeichert werden',
                            onRetry: () => handleSaveChanges(),
                        }
                    );
                } else {
                    await client.refetchQueries({
                        include: ["GetStudio"],
                    });
                    trackStudioUpdated({
                        studioId: studio?.id,
                        section: 'availability',
                        source: 'studio_editor'
                    });
                    setRefreshKey(prev => prev + 1);
                }

                setIsUploading(false);
                return !!saveResult;
            }

            // Handle normal field updates
            if (Object.keys(updatedFields).length > 0) {
                const payload = { ...updatedFields };
                let didSaveSomething = false;
                if (Array.isArray(payload.optionalBookableEquipment)) {
                    await syncOptionalEquipmentChanges(payload.optionalBookableEquipment);
                    delete payload.optionalBookableEquipment;
                    didSaveSomething = true;
                }
                Object.keys(payload).forEach((key) => {
                    if (payload[key] === undefined) {
                        delete payload[key];
                    }
                });
                if (Object.keys(payload).length > 0) {
                    await handleStudioUpdate(studio.id, payload, studio);
                    didSaveSomething = true;
                }

                if (didSaveSomething) {
                    trackStudioUpdated({
                        studioId: studio?.id,
                        section: activeComponent,
                        source: 'studio_editor'
                    });
                    clearUpdates();
                    setShowSaveNotification(true);
                }
            }

            setIsUploading(false);
            return true;
        } catch (error) {
            if (error.cleanupError) {
                console.warn('Paid-Equipment-Bild-Cleanup nach fehlgeschlagenem Equipment-Update ist fehlgeschlagen:', error.cleanupError);
            }
            setIsUploading(false);
            setHasError(true);

            const errorMessage = `${error?.message || ""} ${(error?.graphQLErrors || [])
                .map((graphQLError) => graphQLError?.message || "")
                .join(" ")}`.toLowerCase();
            const isBookingConflictError =
                activeComponent === "status" &&
                (errorMessage.includes("buchung") || errorMessage.includes("booking"));

            if (isBookingConflictError && typeof statusValidation.current?.showBookingConflict === "function") {
                statusValidation.current.showBookingConflict();
                return false;
            }

            notifySaveError(error, {
                title: "Fehler beim Speichern",
                onRetry: () => handleSaveChanges(),
            });
            return false;
        }
    };

    useEffect(() => {
        registerSaveHandler?.(handleSaveChanges);
        return () => registerSaveHandler?.(null);
    }, [registerSaveHandler, handleSaveChanges]);

    const handleEquipmentOptionalDirtyChange = useCallback(
        (isDirty) => {
            onEditorDirtyChange?.("equipmentOptional", isDirty);
        },
        [onEditorDirtyChange]
    );

    const renderActiveComponent = () => {
        switch(activeComponent) {
            case 'fotos':
                return <FotosEditor
                            validate={fotosInputValidation}
                            selectedFiles={selectedFiles}
                            setSelectedFiles={setSelectedFiles}
                            studioImages={studio?.originalImages}
                        />;
            case 'titel':
                return <TitleDescription validate={titleDescriptionValidation} studio={studio} />;
            case 'standort':
                return <StandortEditor validate={standortValidation} studio={studio} />;
            case 'studioType':
                return <TypeOfStudioEditor validate={typeOfStudioValidation} studio={studio} />;
            case 'size':
                return <StudioSizeEditor validate={studioSizeValidation} studio={studio} />;
            case 'price':
                return <PreisEditor validate={preisValidation} studio={studio} />;
            // case 'tags':
            //     return <TagsEditor validate={beschreibungValidation} studio={studio} />;
            case 'equipment':
                return <StudioEquipmentEditor validate={studioEquipmentValidation} studio={studio} />;
            case 'amenities':
                return <AmenitiesEditor validate={amenitiesValidation} studio={studio} />;
            case 'cancellation':
                return <Stornierungsbedingungen fromEditor={true} validate={stornierungsbedingungenValidation} studio={studio} />;
            case 'rules':
                return <Rules fromEditor={true} validate={rulesValidation} studio={studio} />;
            case 'availability':
                return <AvailabilityEditor
                    ref={availabilityEditorRef}
                    studio={studio}
                    validate={availabilityValidation}
                    key={refreshKey}
                />;
            case 'equipmentOptional':
                return (
                    <EquipmentOptionalEditor
                        validate={equipmentOptionalValidation}
                        studio={studio}
                        onDirtyChange={handleEquipmentOptionalDirtyChange}
                    />
                );
            case 'status':
                return <StatusEditor validate={statusValidation} studio={studio} />;
            default:
                return <div className={classes.noComponentSelected}>
                    <p>Bitte wählen Sie eine Kategorie aus der Übersicht aus, um sie zu bearbeiten.</p>
                </div>;
        }
    };

    const updateButtonPosition = () => {
            const contentRect = contentRef.current.getBoundingClientRect();
            const rightEdge = window.innerWidth - contentRect?.right;

            setButtonRightPosition(rightEdge);
    };

    useEffect(() => {
        updateButtonPosition();
        const timer = setTimeout(updateButtonPosition, 100);
        return () => clearTimeout(timer);
    }, [activeComponent, windowWidth]);

    const calculateMargin = () => {
        if (windowWidth < 1400) {
            return 0;
        } else if (windowWidth < 1600) {
            return leftPosition > 0 ? `${leftPosition - 444}px` : undefined;
        } else {
            return leftPosition > 0 ? `${leftPosition - 496}px` : undefined;
        }
    };

    return (
    <div className={`${classes.editorContainer} ${isMobileView ? classes.mobileActive : ''}`} style={{ marginLeft: calculateMargin() }}>
        <header className={classes.phoneHeader}>
            <div className={classes.backButton} onClick={handleBackClick}>
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
                    <line x1="9.60104" y1="0.726041" x2="0.602492" y2="9.72459" stroke="#1F1F1F" strokeWidth="1.7"/>
                    <line x1="0.624583" y1="8.54846" x2="9.62313" y2="18.2969" stroke="#1F1F1F" strokeWidth="1.7"/>
                </svg>
            </div>
            <h2 onClick={handleBackClick} className="mediumLarge">Zurück</h2>
        </header>
        <div className={classes.editorContainerInner}>
            <div
                style={{padding: activeComponent === 'equipment' && windowWidth < 430 ? '30px 0px 150px 0px' : ''}}
                className={classes.editorContainerInnerContent}
                ref={contentRef}
            >
                {renderActiveComponent()}
            </div>
        </div>
        <div
            className={classes.buttonContainer}
            style={{ right: `${buttonRightPosition}px` }}
        >
            <button
                className={`${classes.footerButton} mediumLarge`}
                onClick={() => {
                    handleSaveChanges();
                }}
                disabled={loading || isUploading || (activeComponent === 'availability' && hasError)}
            >
                {loading || isUploading ? "Wird gespeichert..." : "Speichern"}
            </button>
        </div>

        <SaveNotification
            show={showSaveNotification}
            onHide={() => setShowSaveNotification(false)}
        />
    </div>
    )
};

export default EditorContainer;
